<template>
  <div>
    <slot v-if="showActions">
      <actions :account="account" />
    </slot>
    <insights :account="account" class="q-py-lg" />
    <q-separator />
    <latest-post-import
      v-if="latestImport"
      :post-import="latestImport"
      :platform="account.socialSite.name"
      class="q-pa-lg"
      @running="isImporting = true"
      @successful="onImportSuccess"
      @failed="isImporting = false"
    />
    <app-user v-slot="{ isServiceProvider, isCustomer }">
      <template v-if="isServiceProvider">
        <q-separator />
        <edit-username
          :account="account"
          @update:account="$emit('update:account', $event)"
        />
      </template>
      <q-separator v-if="isServiceProvider || isCustomer" />
      <delete-account
        v-if="isServiceProvider || isCustomer"
        :account="account"
        class="q-pa-lg"
      />
    </app-user>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { Account, PostImport as PostImportType } from '@/social/types';
import AppUser from '@/auth/AppUser.vue';
import eventBus from '@/messaging/event-bus';
import { PostImportCreated } from '@/social/post/import';
import Actions from './action/Actions.vue';
import Insights from './insights/Insights.vue';
import DeleteAccount from './DeleteAccount.vue';
import EditUsername from './EditUsername.vue';
import LatestPostImport from '../post/import/latest/LatestPostImport.vue';

interface Data {
  isImporting?: boolean
}

export default defineComponent({
  emits: ['update:account'],
  components: {
    LatestPostImport,
    Insights,
    DeleteAccount,
    EditUsername,
    AppUser,
    Actions,
  },
  props: {
    account: {
      type: Object as () => Account,
      required: true,
    },
  },
  data(): Data {
    return {
      isImporting: undefined,
    };
  },
  computed: {
    latestImport(): PostImportType|null {
      return this.account.latestImport;
    },
    neverImported(): boolean {
      return (this.latestImport === null);
    },
    showActions(): boolean {
      if (this.neverImported) return true;
      return (this.isImporting === false);
    },
  },
  watch: {
    'account.id': {
      handler(): void {
        this.isImporting = undefined;
      },
    },
  },
  methods: {
    onImportCreated(event: PostImportCreated): void {
      if (event.postImport.accountId === this.account.id) {
        this.isImporting = true;
      }
    },
    onImportSuccess(latestImport: PostImportType) {
      this.isImporting = false;
      if (this.latestImport && this.latestImport.completedAt) return;

      this.$emit('update:account', {
        ...this.account,
        latestImport,
        postsUntil: latestImport.until || this.account.postsUntil,
        reconnectRequest: null,
      });
    },
  },
  created(): void {
    eventBus.$on(PostImportCreated.eventName, this.onImportCreated);
  },
  beforeUnmount(): void {
    eventBus.$off(PostImportCreated.eventName, this.onImportCreated);
  },
});
</script>
